import { isEqual } from 'lodash-es'
import { TableKey } from '@tradecafe/types/core'
import { createEntityAdapter, EntityState } from '@ngrx/entity'
import { createReducer, createSelector, on } from '@ngrx/store'

import { State } from '../reducers'
import { DeepReadonly } from '@tradecafe/types/utils'
import * as CompanyViewActions from './company-view.actions'
import { ElasticSearchFilters } from 'src/services/elastic-search'
import { prepareCompaniesPayload } from './company-view-filters.utils'
import { AccountRow } from 'src/services/data/account-elastic.service'

export const companyViewsFeatureKey = 'companyViews'

export interface CompanyViewsState extends EntityState<AccountRow> {
  filters: Dictionary<ElasticSearchFilters>
  prevQuery: Dictionary<any>
  // company ids lists grouped by table key
  tables: Dictionary<number[]>
  // total companies lists grouped by table key
  totals: Dictionary<Dictionary<any>>
}

const selectId = (row: AccountRow) => {
  return row.account
}
const adapter = createEntityAdapter<AccountRow>({ selectId })

const initialState: CompanyViewsState = adapter.getInitialState({
  tables: {},
  totals: {},
  filters: {},
  prevQuery: {},
})

export const companyViewReducer = createReducer(
  initialState,
  on(CompanyViewActions.loadCompanyViews,
    (state, action) => {
      const key = action.tableKey
      const query = prepareCompaniesPayload(action.filters, action.page)
      const prev = state.prevQuery[key] || {}

      let { tables, prevQuery } = state
      if (!isEqual(query.query, prev.query)) {
        tables = { ...tables, [key]: undefined } // drop table
        prevQuery = { ...prevQuery, [key]: query } // save query

      } else if (!isEqual(query.sort, prev.sort)) {
        const table = [...tables[key] || []]
        tables = { ...tables, [key]: table.fill(undefined) } // clear table
        prevQuery = { ...prevQuery, [key]: query } // save query
      }
      return { ...state, tables, totals: {}, prevQuery }
    }),
  on(CompanyViewActions.loadCompanyViewsSuccess,
    (state, action) => {
      const key = action.tableKey
      const { total, totals: totals_, companyViews, payload: { skip } } = action
      const companyIds = companyViews.map(selectId)

      let table = Array.from<number>({ length: total }).fill(undefined)
      table.splice(skip, companyIds.length, ...companyIds)
      const tables = { ...state.tables, [key]: table }
      const totals = { ...state.totals, [key]: totals_ }

      state = adapter.upsertMany(action.companyViews, state)
      return { ...state, tables, totals }
    }),
  on(CompanyViewActions.loadCompanyFiltersSuccess,
    (state, action) => {
      const key = action.tableKey
      const filters = { ...state.filters, [key]: action.result }
      return { ...state, filters }
    }),
)

const selectState = (state: State) => state[companyViewsFeatureKey]
export const { selectEntities: selectCompanyViewEntities } = adapter.getSelectors(selectState)

export const selectCompany = (accountId: number) => createSelector(
  selectCompanyViewEntities,
  (companyViews: Dictionary<AccountRow>) => companyViews[accountId]
)

export const selectCompaniesByIds = (accountIds: DeepReadonly<number[]>) => createSelector(
  selectCompanyViewEntities,
  (companyViews: Dictionary<AccountRow>) => accountIds.map(accountId => companyViews[accountId])
)

const selectTables = createSelector(selectState, state => state.tables)

export const selectCompaniesFilters = (key: TableKey) => createSelector(
  selectState,
  state => state.filters[key],
)

export const selectCompaniesTotals = (key: TableKey) => createSelector(
  selectState,
  state => state.totals[key],
)

export const selectCompaniesTable = (key: TableKey) => createSelector(
  selectCompanyViewEntities,
  selectTables,
  (companyViews, tables) => tables[key] ? tables[key].map(id => companyViews[id]) : undefined
)
